import {

  clearGeolocationCache,

  loadGeolocationCache,

  saveGeolocationCache,

} from "@/src/modules/core/lib/geolocation-cache";

import {formatGpsCoordinates, parseGpsCoordinatesString} from "@/src/modules/core/lib/format-gps-coordinates";

import {

  GEOLOCATION_PERMISSION_DENIED_CODE,

  getBlockedRecoveryIntervalMs,

  getGeolocationPlatform,

  getRecheckProbeTimeoutMs,

  getRecoveryProbeTimeoutMs,

  getSheetOpenProbeTimeoutMs,

  getSyncDebounceMs,

  isNativeMobileGeolocationPlatform,

  shouldForceProbeOnVisibility,

  shouldProbeBeforeTrustingPermissionState,

} from "@/src/modules/core/lib/geolocation-platform";

import {

  GEOLOCATION_STALE_MS,

  GEOLOCATION_VISIBILITY_REFRESH_MS,

  WATCH_BUFFER_MAX_AGE_DESKTOP_MS,

  WATCH_BUFFER_MAX_AGE_MOBILE_MS,

  getSubmitHighAccuracyOptions,
  getSheetOpenProbeOptions,
  getWatchPositionOptions,

  isWatchBufferOnMobileDevice,

  SUBMIT_MOBILE_COARSE_OPTIONS,

} from "@/src/modules/core/lib/geolocation-profiles";

import {getGeolocationState, useGeolocationStore} from "@/src/modules/core/store/geolocation";

import type {

  AcquirePositionOptions,

  GeolocationFix,

  GeolocationStatus,

} from "@/src/modules/core/types/geolocation";



const ERROR_CODES = {

  PERMISSION_DENIED: GEOLOCATION_PERMISSION_DENIED_CODE,

  POSITION_UNAVAILABLE: 2,

  TIMEOUT: 3,

} as const;



type SyncPermissionOptions = {

  forceProbe?: boolean;

  probeTimeoutMs?: number;

  /** Skip debounce — sheet open, lifecycle return, blocked recovery. */

  urgent?: boolean;

};



class GeolocationService {

  private watchId: number | null = null;

  private acquireInFlight: Promise<GeolocationFix> | null = null;

  private syncInFlight: Promise<void> | null = null;

  private permissionResult: PermissionStatus | null = null;

  private onPermissionChange: (() => void) | null = null;

  private sessionActive = false;

  private lastSyncCompletedAt = 0;

  private blockedRecoveryIntervalId: number | null = null;

  private blockedRecoveryUnsubscribe: (() => void) | null = null;

  private onBlockedRecoveryVisibility: (() => void) | null = null;

  /** Ignores stale watchPosition callbacks after clearWatch/restart (common on iOS). */
  private watchGeneration = 0;



  /** One session: watch + permission monitor + lifecycle + blocked recovery polling. */

  startSession(): void {

    if (this.sessionActive) {

      return;

    }

    this.sessionActive = true;

    this.startWatch();

    this.startPermissionMonitor();

    this.attachLifecycleListeners();

    this.startBlockedRecoveryMonitor();

  }



  stopSession(): void {

    if (!this.sessionActive) {

      return;

    }

    this.sessionActive = false;

    this.stopBlockedRecoveryMonitor();

    this.detachLifecycleListeners();

    this.stopPermissionMonitor();

    this.clearWatch();

    this.patchState({

      status: "idle",

      isWatching: false,

      error: null,

    });

  }



  startWatch(): void {

    if (typeof navigator === "undefined" || !navigator.geolocation) {

      this.patchState({status: "unavailable", isWatching: false});

      return;

    }



    if (this.watchId != null) {

      return;

    }



    const cachedFix = loadGeolocationCache();

    const trustCacheOnStartup = cachedFix && !shouldProbeBeforeTrustingPermissionState();



    if (trustCacheOnStartup) {

      this.applyFix(cachedFix, {persist: false});

    } else {

      this.patchState({

        status: "acquiring",

        isWatching: true,

        error: null,

        fix: null,

      });

    }



    this.beginWatch();



    this.patchState({isWatching: true});

    void this.syncPermissionState({

      urgent: true,

      forceProbe: shouldProbeBeforeTrustingPermissionState(),

      probeTimeoutMs: getRecoveryProbeTimeoutMs(),

    });

  }



  startPermissionMonitor(): void {

    if (typeof navigator === "undefined" || !navigator.permissions?.query) {

      return;

    }



    void navigator.permissions

      .query({name: "geolocation" as PermissionName})

      .then((result) => {

        this.permissionResult = result;

        void this.syncPermissionState({

          urgent: true,

          forceProbe: shouldProbeBeforeTrustingPermissionState(),

          probeTimeoutMs: getRecoveryProbeTimeoutMs(),

        });



        const onChange = () => {

          void this.syncPermissionState({

            urgent: true,

            forceProbe: true,

            probeTimeoutMs: getRecoveryProbeTimeoutMs(),

          });

        };

        this.onPermissionChange = onChange;

        result.addEventListener("change", onChange);

      })

      .catch(() => {

        // Geolocation permission name unsupported in this browser context.

      });

  }



  stopPermissionMonitor(): void {

    if (this.permissionResult && this.onPermissionChange) {

      this.permissionResult.removeEventListener("change", this.onPermissionChange);

    }

    this.permissionResult = null;

    this.onPermissionChange = null;

  }



  /**

   * Sync store with browser permission + a real position probe when needed.

   * iOS Safari often reports stale permission state; probing is the source of truth.

   */

  async syncPermissionState(options: SyncPermissionOptions = {}): Promise<void> {

    if (this.syncInFlight) {

      return this.syncInFlight;

    }



    this.syncInFlight = this.syncPermissionStateInternal(options).finally(() => {

      this.syncInFlight = null;

    });



    return this.syncInFlight;

  }



  /** Permissions API when available; `unknown` when unsupported (e.g. some Firefox contexts). */

  async queryGeolocationPermissionState(): Promise<PermissionState | "unknown"> {

    if (typeof navigator === "undefined" || !navigator.permissions?.query) {

      return "unknown";

    }



    try {

      const result = await navigator.permissions.query({

        name: "geolocation" as PermissionName,

      });

      return result.state;

    } catch {

      return "unknown";

    }

  }



  private async syncPermissionStateInternal(

    options: SyncPermissionOptions = {},

  ): Promise<void> {

    if (typeof navigator === "undefined") {

      return;

    }



    if (

      !options.urgent &&

      Date.now() - this.lastSyncCompletedAt < getSyncDebounceMs()

    ) {

      return;

    }



    const wasDenied = isLocationCaptureBlocked();

    const shouldProbe =

      options.forceProbe === true ||

      wasDenied ||

      shouldProbeBeforeTrustingPermissionState();



    if (navigator.permissions?.query && !shouldProbe) {

      try {

        const result = await navigator.permissions.query({

          name: "geolocation" as PermissionName,

        });

        await this.applyPermissionQueryState(result.state);

        this.lastSyncCompletedAt = Date.now();

        return;

      } catch {

        // Fall through to geolocation probe.

      }

    }



    if (!navigator.geolocation) {

      this.patchState({status: "unavailable", isWatching: false});

      this.lastSyncCompletedAt = Date.now();

      return;

    }



    if (wasDenied) {

      this.clearWatch();

    }



    const recovered = await this.probeDevicePosition({

      timeoutMs:

        options.probeTimeoutMs ??

        (shouldProbe ? getRecoveryProbeTimeoutMs() : 8_000),

    });

    if (recovered) {

      this.restartWatch();

    } else if (wasDenied && !isLocationCaptureBlocked()) {

      this.beginWatch();

    }



    this.lastSyncCompletedAt = Date.now();

  }



  /** @deprecated Prefer stopSession() — kept for narrow teardown in tests. */

  stopWatch(): void {

    this.stopPermissionMonitor();

    this.clearWatch();

    this.patchState({

      status: "idle",

      isWatching: false,

      error: null,

    });

  }



  private clearWatch(): void {

    this.watchGeneration += 1;

    if (this.watchId != null && typeof navigator !== "undefined" && navigator.geolocation) {

      navigator.geolocation.clearWatch(this.watchId);

    }

    this.watchId = null;

  }



  private beginWatch(): void {

    if (typeof navigator === "undefined" || !navigator.geolocation) {

      return;

    }



    const generation = this.watchGeneration;

    this.watchId = navigator.geolocation.watchPosition(

      (position) => {

        if (generation !== this.watchGeneration) {

          return;

        }

        this.applyFix(this.positionToFix(position, "device"));

      },

      (error) => {

        if (generation !== this.watchGeneration) {

          return;

        }

        this.handlePositionError(error);

      },

      getWatchPositionOptions(),

    );

  }



  getLastFix(): GeolocationFix | null {

    return getGeolocationState().fix;

  }



  /**
   * Keep the session watch alive and restart it when the submit buffer is empty or stale.
   * Call before gate prewarm so submit can reuse a recent device fix.
   */
  prepareForGatePrewarm(): void {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return;
    }

    if (isLocationCaptureBlocked()) {
      return;
    }

    this.startSession();

    const buffered = this.getLastFix();
    if (!buffered || buffered.source !== "device" || !this.isWatchBufferFresh(buffered)) {
      this.restartWatch();
    }
  }



  /** Await an in-flight gate prewarm so submit reuses it instead of a parallel fresh read. */
  async awaitInFlightAcquire(): Promise<GeolocationFix | null> {
    if (!this.acquireInFlight) {
      return null;
    }

    try {
      return await this.acquireInFlight;
    } catch {
      return null;
    }
  }



  /**
   * Skip sheet gate probe when the session watch already has a live device fix.
   * Requires no recent error — avoids trusting stale "ready" after timeout/deny.
   */
  canSkipRequiredSheetProbe(): boolean {
    if (isLocationCaptureBlocked()) {
      return false;
    }

    const {status, fix, isWatching, error} = getGeolocationState();

    if (error) {
      return false;
    }

    if (status !== "ready" || !fix || fix.source !== "device" || !isWatching) {
      return false;
    }

    return this.isWatchBufferFresh(fix);
  }



  async acquirePosition(options: AcquirePositionOptions = {}): Promise<GeolocationFix> {

    if (isLocationCaptureBlocked()) {

      throw new Error("Geolocation permission denied");

    }



    if (this.acquireInFlight) {

      return this.acquireInFlight;

    }



    this.acquireInFlight = this.acquirePositionInternal(options).finally(() => {

      this.acquireInFlight = null;

    });



    return this.acquireInFlight;

  }



  async refresh(): Promise<void> {

    if (typeof navigator === "undefined" || !navigator.geolocation) {

      this.patchState({status: "unavailable"});

      return;

    }



    if (isLocationCaptureBlocked()) {

      return;

    }



    this.patchState({status: "acquiring", error: null});



    try {

      const position = await this.getCurrentPosition(getSubmitHighAccuracyOptions());

      this.applyFix(this.positionToFix(position, "device"));

    } catch (error) {

      this.handlePositionError(this.toPositionError(error));

    }

  }



  onVisibilityVisible(): void {

    if (shouldForceProbeOnVisibility()) {
      const {fix, status} = getGeolocationState();
      if (status !== "denied" && fix?.source === "device") {
        this.patchState({fix: null, error: null, status: "acquiring"});
      }
    }

    const shouldForceProbe =

      isLocationCaptureBlocked() || shouldForceProbeOnVisibility();

    void this.syncPermissionState({

      urgent: true,

      forceProbe: shouldForceProbe,

      probeTimeoutMs: getRecoveryProbeTimeoutMs(),

    }).then(() => {

      const {fix, status} = getGeolocationState();

      if (status === "denied") {

        return;

      }



      const ageMs = fix ? Date.now() - fix.timestamp : Number.POSITIVE_INFINITY;

      if (ageMs > GEOLOCATION_VISIBILITY_REFRESH_MS) {

        void this.refresh();

      }

    });

  }



  formatCoordinates(fix: GeolocationFix): string {

    const formatted = formatGpsCoordinates(fix);

    if (!formatted) {

      throw new Error("Invalid geolocation fix");

    }

    return formatted;

  }



  isWatchBufferFresh(fix: GeolocationFix, now = Date.now()): boolean {

    const maxAge = isWatchBufferOnMobileDevice()

      ? WATCH_BUFFER_MAX_AGE_MOBILE_MS

      : WATCH_BUFFER_MAX_AGE_DESKTOP_MS;

    return now - fix.timestamp <= maxAge;

  }



  private async acquirePositionInternal(

    options: AcquirePositionOptions,

  ): Promise<GeolocationFix> {

    const {

      fresh = true,

      allowCache = true,

      allowPropertyFallback = false,

      propertyCoordinates = null,

    } = options;



    if (isLocationCaptureBlocked()) {

      throw new Error("Geolocation permission denied");

    }



    if (!fresh) {

      const buffered = this.getLastFix();

      if (buffered && this.isWatchBufferFresh(buffered)) {

        return buffered;

      }

    }



    try {

      const deviceFix = await this.readDevicePosition(options);

      this.applyFix(deviceFix);

      return deviceFix;

    } catch {

      // Fall through to cache/property chain when permission is granted.

    }



    if (isLocationCaptureBlocked()) {

      throw new Error("Geolocation permission denied");

    }



    if (allowCache) {

      const cachedFix = loadGeolocationCache();

      if (cachedFix) {

        this.applyFix(cachedFix, {persist: false});

        return cachedFix;

      }

    }



    if (allowPropertyFallback) {

      const propertyFix = parseGpsCoordinatesString(propertyCoordinates, "property");

      if (propertyFix) {

        return propertyFix;

      }

    }



    throw new Error("Unable to acquire geolocation fix");

  }



  private async readDevicePosition(options: AcquirePositionOptions): Promise<GeolocationFix> {

    if (typeof navigator === "undefined" || !navigator.geolocation) {

      throw new Error("Geolocation is not supported");

    }



    const isMobile = isNativeMobileGeolocationPlatform();

    const primaryOptions: PositionOptions = {

      ...getSubmitHighAccuracyOptions(),

      ...(options.highAccuracy != null

        ? {enableHighAccuracy: options.highAccuracy}

        : {}),

      ...(options.timeoutMs != null ? {timeout: options.timeoutMs} : {}),

      ...(options.maxAgeMs != null ? {maximumAge: options.maxAgeMs} : {}),

    };



    try {

      const position = await this.getCurrentPosition(primaryOptions);

      return this.positionToFix(position, "device");

    } catch (error) {

      const positionError = this.toPositionError(error);

      const shouldRetryCoarse =

        options.allowCoarseRetry !== false &&

        isMobile &&

        (positionError.code === ERROR_CODES.TIMEOUT ||

          positionError.code === ERROR_CODES.POSITION_UNAVAILABLE);



      if (!shouldRetryCoarse) {

        throw positionError;

      }



      const coarsePosition = await this.getCurrentPosition(SUBMIT_MOBILE_COARSE_OPTIONS);

      return this.positionToFix(coarsePosition, "device");

    }

  }



  private getCurrentPosition(options: PositionOptions): Promise<GeolocationPosition> {

    const timeoutMs = (options.timeout ?? 15_000) + 2_000;



    return new Promise((resolve, reject) => {

      let settled = false;



      const timer = window.setTimeout(() => {

        if (settled) {

          return;

        }

        settled = true;

        reject(this.createTimeoutError());

      }, timeoutMs);



      navigator.geolocation.getCurrentPosition(

        (position) => {

          if (settled) {

            return;

          }

          settled = true;

          window.clearTimeout(timer);

          resolve(position);

        },

        (error) => {

          if (settled) {

            return;

          }

          settled = true;

          window.clearTimeout(timer);

          reject(error);

        },

        options,

      );

    });

  }



  private createTimeoutError(): GeolocationPositionError {

    return {

      code: ERROR_CODES.TIMEOUT,

      message: "Geolocation request timed out",

      PERMISSION_DENIED: ERROR_CODES.PERMISSION_DENIED,

      POSITION_UNAVAILABLE: ERROR_CODES.POSITION_UNAVAILABLE,

      TIMEOUT: ERROR_CODES.TIMEOUT,

    };

  }



  private positionToFix(

    position: GeolocationPosition,

    source: GeolocationFix["source"],

  ): GeolocationFix {

    return {

      latitude: position.coords.latitude,

      longitude: position.coords.longitude,

      accuracy: position.coords.accuracy ?? null,

      timestamp: position.timestamp,

      source,

    };

  }



  private applyFix(fix: GeolocationFix, options: {persist?: boolean} = {}): void {

    const {persist = true} = options;

    if (persist && fix.source === "device") {

      saveGeolocationCache(fix);

    }



    this.patchState({

      fix,

      error: null,

      status: this.deriveStatus(fix),

    });

  }



  private handlePositionError(error: GeolocationPositionError): void {

    const {fix} = getGeolocationState();

    const status = this.errorToStatus(error.code, fix);



    if (error.code === ERROR_CODES.PERMISSION_DENIED) {

      clearGeolocationCache();

      this.clearWatch();

      this.patchState({

        error,

        status: "denied",

        fix: null,

        isWatching: false,

      });

      return;

    }



    this.patchState({

      error,

      status,

    });

  }



  private deriveStatus(fix: GeolocationFix, now = Date.now()): GeolocationStatus {

    if (now - fix.timestamp > GEOLOCATION_STALE_MS) {

      return "stale";

    }

    return "ready";

  }



  private errorToStatus(

    code: number,

    fix: GeolocationFix | null,

  ): GeolocationStatus {

    if (code === ERROR_CODES.PERMISSION_DENIED) {

      return "denied";

    }

    if (code === ERROR_CODES.TIMEOUT) {

      return fix ? this.deriveStatus(fix) : "timeout";

    }

    if (code === ERROR_CODES.POSITION_UNAVAILABLE) {

      return fix ? this.deriveStatus(fix) : "unavailable";

    }

    return fix ? this.deriveStatus(fix) : "unavailable";

  }



  private createPermissionDeniedError(): GeolocationPositionError {

    return {

      code: ERROR_CODES.PERMISSION_DENIED,

      message: "Geolocation permission denied",

      PERMISSION_DENIED: ERROR_CODES.PERMISSION_DENIED,

      POSITION_UNAVAILABLE: ERROR_CODES.POSITION_UNAVAILABLE,

      TIMEOUT: ERROR_CODES.TIMEOUT,

    };

  }



  private async applyPermissionQueryState(state: PermissionState): Promise<void> {

    const wasDenied = isLocationCaptureBlocked();



    if (state === "denied") {

      if (shouldProbeBeforeTrustingPermissionState()) {

        this.clearWatch();

        const recovered = await this.probeDevicePosition({

          timeoutMs: getRecoveryProbeTimeoutMs(),

        });

        if (recovered) {

          this.restartWatch();

        } else if (!isLocationCaptureBlocked()) {

          clearGeolocationCache();

          this.patchState({

            status: "denied",

            error: this.createPermissionDeniedError(),

            fix: null,

            isWatching: false,

          });

        }

        return;

      }

      clearGeolocationCache();

      this.patchState({

        status: "denied",

        error: this.createPermissionDeniedError(),

        fix: null,

        isWatching: false,

      });

      return;

    }



    if (state === "granted") {

      if (wasDenied || shouldProbeBeforeTrustingPermissionState()) {

        const recovered = await this.probeDevicePosition({

          timeoutMs: getRecoveryProbeTimeoutMs(),

        });

        if (recovered) {

          this.restartWatch();

        }

        return;

      }

      await this.refresh();

      return;

    }



    if (wasDenied) {

      const recovered = await this.probeDevicePosition({

        timeoutMs: getRecoveryProbeTimeoutMs(),

      });

      if (recovered) {

        this.restartWatch();

      }

    }

  }



  /** Real position read — source of truth when Permissions API is stale (common on iOS). */

  private async probeDevicePosition(
    options: {timeoutMs?: number; singleStrategy?: boolean} = {},
  ): Promise<boolean> {

    if (typeof navigator === "undefined" || !navigator.geolocation) {

      return false;

    }



    const timeoutMs = options.timeoutMs ?? getRecoveryProbeTimeoutMs();

    const probeStrategies: PositionOptions[] =

      getGeolocationPlatform() === "ios"

        ? [

            {enableHighAccuracy: true, maximumAge: 0, timeout: timeoutMs},

            {enableHighAccuracy: false, maximumAge: 0, timeout: timeoutMs},

          ]

        : [

            {

              enableHighAccuracy: isNativeMobileGeolocationPlatform(),

              maximumAge: 0,

              timeout: timeoutMs,

            },

          ];

    const strategies = options.singleStrategy
      ? [getSheetOpenProbeOptions(timeoutMs)]
      : probeStrategies;



    let lastError: GeolocationPositionError | null = null;



    for (const strategy of strategies) {

      try {

        const position = await this.getCurrentPosition(strategy);

        this.applyFix(this.positionToFix(position, "device"));

        return true;

      } catch (error) {

        lastError = this.toPositionError(error);

        if (lastError.code === ERROR_CODES.PERMISSION_DENIED) {

          this.handlePositionError(lastError);

          return false;

        }

      }

    }



    if (lastError) {

      this.handlePositionError(lastError);

    }

    return false;

  }



  /**
   * Sheet open gate — true only after a live device read succeeds.
   * Stale cache / timeout / unavailable must not pass (iOS often never sets "denied").
   */
  async probeForRequiredSheetAccess(
    options: {timeoutMs?: number; singleStrategy?: boolean} = {},
  ): Promise<boolean> {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return false;
    }

    if (isLocationCaptureBlocked()) {
      this.clearWatch();
    }

    const recovered = await this.probeDevicePosition({
      timeoutMs: options.timeoutMs ?? getSheetOpenProbeTimeoutMs(),
      singleStrategy: options.singleStrategy ?? true,
    });

    if (recovered) {
      this.restartWatch();
      return true;
    }

    return false;
  }



  /**
   * After the user enables location in Settings — reset stale deny state and probe hard.
   * iOS PWA often keeps denied in memory until watch teardown + fresh read.
   */
  async recheckLocationForRequiredSheet(): Promise<boolean> {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return false;
    }

    const timeoutMs = getRecheckProbeTimeoutMs();

    this.clearWatch();
    this.patchState({
      status: "acquiring",
      error: null,
      fix: null,
      isWatching: false,
    });

    if (navigator.permissions?.query) {
      try {
        await navigator.permissions.query({name: "geolocation" as PermissionName});
      } catch {
        // Unsupported — probe is still the source of truth.
      }
    }

    const attemptProbe = async (): Promise<boolean> => {
      const recovered = await this.probeDevicePosition({timeoutMs});
      if (recovered) {
        this.restartWatch();
        return true;
      }
      return false;
    };

    if (await attemptProbe()) {
      return true;
    }

    if (getGeolocationPlatform() === "ios") {
      this.clearWatch();
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 400);
      });
      return attemptProbe();
    }

    return false;
  }



  /** iOS often stops delivering watch callbacks after deny → grant without a new watch. */

  private restartWatch(): void {

    if (typeof navigator === "undefined" || !navigator.geolocation) {

      return;

    }



    this.clearWatch();

    this.beginWatch();

    this.patchState({isWatching: true, error: null});

  }



  private readonly onLifecycleSync = (): void => {

    void this.syncPermissionState({

      urgent: true,

      forceProbe: true,

      probeTimeoutMs: getRecoveryProbeTimeoutMs(),

    });

  };



  private readonly onDocumentVisibility = (): void => {

    if (document.visibilityState === "visible") {

      this.onVisibilityVisible();

    }

  };



  private attachLifecycleListeners(): void {

    window.addEventListener("focus", this.onLifecycleSync);

    window.addEventListener("pageshow", this.onLifecycleSync);

    document.addEventListener("visibilitychange", this.onDocumentVisibility);

  }



  private detachLifecycleListeners(): void {

    window.removeEventListener("focus", this.onLifecycleSync);

    window.removeEventListener("pageshow", this.onLifecycleSync);

    document.removeEventListener("visibilitychange", this.onDocumentVisibility);

  }



  private startBlockedRecoveryMonitor(): void {

    const probeWhileBlocked = (): void => {

      if (document.visibilityState !== "visible" || !isLocationCaptureBlocked()) {

        return;

      }



      void this.syncPermissionState({

        urgent: true,

        forceProbe: true,

        probeTimeoutMs: getRecoveryProbeTimeoutMs(),

      });

    };



    const updateRecoveryPolling = (): void => {

      const shouldPoll =

        document.visibilityState === "visible" &&

        (isLocationCaptureBlocked() || isNativeMobileGeolocationPlatform());



      if (shouldPoll && this.blockedRecoveryIntervalId == null) {

        probeWhileBlocked();

        this.blockedRecoveryIntervalId = window.setInterval(

          probeWhileBlocked,

          getBlockedRecoveryIntervalMs(),

        );

      } else if (!shouldPoll && this.blockedRecoveryIntervalId != null) {

        window.clearInterval(this.blockedRecoveryIntervalId);

        this.blockedRecoveryIntervalId = null;

      }

    };



    this.onBlockedRecoveryVisibility = updateRecoveryPolling;

    this.blockedRecoveryUnsubscribe = useGeolocationStore.subscribe(updateRecoveryPolling);

    document.addEventListener("visibilitychange", updateRecoveryPolling);

    updateRecoveryPolling();

  }



  private stopBlockedRecoveryMonitor(): void {

    if (this.blockedRecoveryUnsubscribe) {

      this.blockedRecoveryUnsubscribe();

      this.blockedRecoveryUnsubscribe = null;

    }

    if (this.onBlockedRecoveryVisibility) {

      document.removeEventListener("visibilitychange", this.onBlockedRecoveryVisibility);

      this.onBlockedRecoveryVisibility = null;

    }

    if (this.blockedRecoveryIntervalId != null) {

      window.clearInterval(this.blockedRecoveryIntervalId);

      this.blockedRecoveryIntervalId = null;

    }

  }



  private patchState(

    partial: Partial<ReturnType<typeof getGeolocationState>>,

  ): void {

    useGeolocationStore.setState(partial);

  }



  private toPositionError(error: unknown): GeolocationPositionError {

    if (error && typeof error === "object" && "code" in error) {

      return error as GeolocationPositionError;

    }



    return {

      code: ERROR_CODES.POSITION_UNAVAILABLE,

      message: error instanceof Error ? error.message : "Position unavailable",

      PERMISSION_DENIED: ERROR_CODES.PERMISSION_DENIED,

      POSITION_UNAVAILABLE: ERROR_CODES.POSITION_UNAVAILABLE,

      TIMEOUT: ERROR_CODES.TIMEOUT,

    };

  }

}



export const geolocationService = new GeolocationService();



/** True when browser/site location permission blocks required GPS capture. */

export function isLocationCaptureBlocked(): boolean {

  const {status, error} = getGeolocationState();

  return (

    status === "denied" || error?.code === GEOLOCATION_PERMISSION_DENIED_CODE

  );

}



export function isGeolocationPermissionDenied(): boolean {

  return isLocationCaptureBlocked();

}



export {clearGeolocationCache};


